import type { Post } from "@/lib/news/types";
import hero from "@/assets/news/kontokredit-utan-uc.jpg";

/** Aktörer som erbjuder kontokredit eller flexibel kredit utan UC. */
const KONTOKREDIT_UTAN_UC = [
  "fairlo", "northmill", "brixokk", "flexkonto", "credifi", "lumify",
  "merax", "creditstar", "loanstep", "ferratum", "tomly", "saldo", "klaralan",
];

/** Näst bästa alternativen när en kontokredit inte räcker. */
const NAST_BAST = ["lendo", "enklare", "sambla", "advisa", "axo"];

export const KONTOKREDIT_UTAN_UC_2026: Record<string, Post> = {
  "kontokredit-utan-uc-jamforelse-2026": {
    title: "Kontokredit utan UC 2026 – jämförelse av alla kreditgivare",
    intro:
      "Vill du ha en buffert som finns där när det behövs, utan att en UC-förfrågan syns i din historik? Vi förstår tanken – en kontokredit kostar ingenting när den är oanvänd och räddar månaden när tvättmaskinen går sönder. Här jämför vi alla kreditgivare som erbjuder kontokredit utan UC 2026, vad räntan faktiskt blir i kronor, vilka krav som gäller och när en kontokredit är ett smartare val än ett vanligt lån.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "4 september 2026",
    updated: "4 september 2026",
    heroSrc: hero,
    heroAlt: "Person jämför kontokredit utan UC i mobilen med kort och kvitton på bordet",
    topLenderId: "fairlo",
    bottomLenderId: "northmill",
    topLenderTitle: "Bäst i test – kontokredit utan UC 2026",
    bottomLenderTitle: "Vår rekommendation för högre kreditgräns",
    reviewsLenderId: "northmill",
    cta: { to: "/lan-utan-uc", label: "Jämför kontokrediter utan UC" },
    calculator: {
      title: "Räkna på kontokredit utan UC",
      intro:
        "Dra i reglagen och se vad ett utnyttjat kreditutrymme kostar per månad, hur mycket räntan blir totalt och vilken effektiv ränta du faktiskt betalar.",
      note:
        "En kontokredit kostar bara när du använder den, men räntan räknas dag för dag på det utnyttjade beloppet. Månadskostnaden styrs av kreditbelopp, återbetalningstid och nominell ränta, medan den effektiva räntan visar den verkliga kostnaden eftersom uppläggningsavgift och aviavgift räknas in. Betala tillbaka snabbare än minimibeloppet så sjunker den totala lånekostnaden rejält.",
      amount: 20000,
      minAmount: 1000,
      maxAmount: 60000,
      months: 24,
      maxMonths: 60,
      rate: 19.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 0,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Vad är en kontokredit utan UC?",
        a: "Det är ett förhandsgodkänt kreditutrymme som du kan använda när du vill, där kreditgivaren tar upplysning via Creditsafe eller Dun & Bradstreet i stället för UC. Kreditprövning görs alltid, men förfrågan syns inte i din UC-historik.",
      },
      {
        q: "Kostar en kontokredit något när den är oanvänd?",
        a: "Hos de flesta seriösa aktörer nej – du betalar bara ränta på det belopp du faktiskt utnyttjat. Vissa tar dock en årsavgift eller aviavgift, så läs villkoren innan du tecknar.",
      },
      {
        q: "Hur hög kan kreditgränsen bli utan UC?",
        a: "Vanligtvis mellan 5 000 och 60 000 kr. Nya kunder får ofta en lägre gräns som kan höjas efter några månader med skötsam återbetalning.",
      },
      {
        q: "Är kontokredit billigare än ett snabblån?",
        a: "Ofta ja, om du använder krediten kortvarigt. Räntan ligger typiskt på 15–25 procent och räknas dag för dag, medan ett snabblån har ränta på hela beloppet under hela löptiden plus uppläggningsavgift.",
      },
      {
        q: "Påverkar en kontokredit mitt bolån?",
        a: "Ja. Banken räknar normalt med hela det beviljade utrymmet som en skuld i bolånekalkylen, även om du inte använt en krona. Säg upp krediter du inte behöver innan du söker bolån.",
      },
      {
        q: "Kan jag få kontokredit med betalningsanmärkning?",
        a: "Det är svårt men inte omöjligt hos aktörer som gör individuell bedömning. Har du en pågående skuld hos Kronofogden avslås ansökan i praktiken alltid.",
      },
      {
        q: "Hur snabbt får jag tillgång till pengarna?",
        a: "Efter beviljande är utrymmet oftast tillgängligt direkt, och uttag når kontot inom minuter hos aktörer med realtidsbetalning. Kvällar och helger fungerar hos dem som betalar ut via Swish eller realtid.",
      },
      {
        q: "Kan jag säga upp krediten när jag vill?",
        a: "Ja. Betala av hela saldot och säg upp avtalet skriftligt eller i appen. Du har också 14 dagars ångerrätt enligt konsumentkreditlagen.",
      },
    ],
    blocks: [
      { t: "h2", text: "Kontokredit utan UC 2026 – kort svar" },
      { t: "p", text: "En kontokredit utan UC är ett flexibelt kreditutrymme där prövningen görs via Creditsafe eller Dun & Bradstreet i stället för UC. Du betalar ränta bara på det du använder, och utrymmet ligger kvar utan kostnad när du inte rör det. Räntan ligger 2026 typiskt mellan 15 och 25 procent." },
      { t: "p", text: "Vi vet att många söker just det här för att de redan har flera förfrågningar i UC och inte vill ha fler. Det är en fullt rimlig anledning – men den bör inte göra dig mindre kritisk mot villkoren." },
      {
        t: "stats",
        items: [
          { label: "Vanlig kreditgräns", value: "5 000–60 000 kr" },
          { label: "Typisk ränta 2026", value: "15–25 %" },
          { label: "Kostnad vid oanvänd kredit", value: "Oftast 0 kr" },
        ],
      },

      { t: "h2", text: "Vad en kontokredit egentligen är" },
      { t: "p", text: "En kontokredit är inte ett lån i klassisk mening. Du får ett tak, exempelvis 20 000 kr, och kan sedan ta ut precis så mycket du behöver, när du behöver. Räntan räknas dag för dag på det utnyttjade beloppet, och när du betalar tillbaka fylls utrymmet på igen." },
      { t: "p", text: "Det är just den återfyllnaden som skiljer en kontokredit från ett vanligt lån. Ett blancolån betalas av och tar slut, medan en kontokredit ligger kvar som en löpande möjlighet." },

      { t: "h2", text: "Så fungerar kreditprövning utan UC" },
      { t: "p", text: "Alla kreditgivare med tillstånd från Finansinspektionen är skyldiga att göra en kreditprövning. Skillnaden ligger i vilket upplysningsföretag de använder. Väljer de Creditsafe eller Dun & Bradstreet registreras ingen förfrågan hos UC." },
      { t: "h3", text: "Vad prövningen faktiskt tittar på" },
      { t: "p", text: "Inkomstuppgifter från Skatteverket, eventuella betalningsanmärkningar, pågående mål hos Kronofogden, antal befintliga krediter och hur många förfrågningar du gjort på senare tid." },
      { t: "h3", text: "Varför det spelar roll" },
      { t: "p", text: "Många förfrågningar i UC på kort tid sänker din kreditvärdighet i sig, oavsett om lånen beviljats eller inte. Genom att välja aktörer utanför UC bevarar du den historiken ren." },

      { t: "h2", text: "Alla kreditgivare med kontokredit utan UC 2026" },
      { t: "p", text: "Nedan hittar du hela listan över aktörer som prövar utan UC och erbjuder kontokredit eller flexibel kredit. Villkoren uppdateras löpande." },
      { t: "lenders", ids: KONTOKREDIT_UTAN_UC, title: "Kontokredit och flexibel kredit utan UC", note: "Samtliga aktörer har tillstånd från Finansinspektionen och prövar via Creditsafe eller Dun & Bradstreet." },

      { t: "h2", text: "Kontokredit eller privatlån – vilket passar dig?" },
      {
        t: "table",
        caption: "Jämförelse mellan kontokredit och privatlån",
        head: ["Egenskap", "Kontokredit", "Privatlån"],
        rows: [
          ["Ränta", "15–25 %", "5–20 %"],
          ["Kostnad när den inte används", "Oftast 0 kr", "Ränta på hela beloppet"],
          ["Uppläggningsavgift", "Ofta ingen", "295–695 kr"],
          ["Belopp", "5 000–60 000 kr", "10 000–600 000 kr"],
          ["Återbetalning", "Fritt, minst minimibeloppet", "Fast plan"],
          ["Passar för", "Buffert och oväntade utgifter", "Planerade köp och samling"],
        ],
      },
      { t: "p", text: "Tumregeln är enkel: behöver du en känd summa vid ett tillfälle är ett privatlån billigare. Behöver du beredskap för något du inte vet omfattningen av är kontokrediten smidigare." },

      { t: "h2", text: "Vad en kontokredit kostar i kronor" },
      { t: "p", text: "Räkneexemplet nedan utgår från 20 000 kr utnyttjad kredit med 19,9 procent nominell ränta, ingen uppläggningsavgift och 29 kr per avi." },
      {
        t: "table",
        caption: "Räkneexempel: 20 000 kr utnyttjad kredit, 19,9 % nominell ränta",
        head: ["Återbetalningstid", "Månadskostnad", "Räntekostnad", "Total kostnad", "Effektiv ränta"],
        rows: [
          ["6 mån", "3 549 kr", "1 172 kr", "21 346 kr", "23,6 %"],
          ["12 mån", "1 852 kr", "2 226 kr", "22 574 kr", "22,7 %"],
          ["24 mån", "1 016 kr", "4 393 kr", "25 089 kr", "22,3 %"],
          ["36 mån", "742 kr", "6 719 kr", "27 763 kr", "22,1 %"],
        ],
      },
      { t: "p", text: "Skillnaden mellan sex och 36 månader är över 6 000 kr. Räkna alltid på din egen situation i vår [lånekalkyl](/guider/lanekalkyl) innan du bestämmer dig." },

      { t: "h2", text: "Effektiv ränta – det enda jämförbara måttet" },
      { t: "p", text: "Nominell ränta säger lite eftersom avgifter ligger utanför. Effektiv ränta räknar in uppläggningsavgift, aviavgift och löptid och är det enda talet som gör två erbjudanden jämförbara." },
      {
        t: "box",
        tone: "tip",
        title: "Så jämför du rätt",
        items: [
          "Läs effektiv ränta, inte månadskostnad.",
          "Kontrollera om aviavgiften försvinner med e-faktura eller autogiro.",
          "Fråga om årsavgift – den förekommer på vissa kreditkonton.",
          "Kolla minimibeloppet per månad, ofta 3–5 procent av saldot.",
        ],
      },

      { t: "h2", text: "Krav för att bli beviljad kontokredit utan UC" },
      {
        t: "ul",
        items: [
          "Minst 18–20 år beroende på kreditgivare.",
          "Folkbokförd i Sverige och svenskt BankID.",
          "Regelbunden och verifierbar inkomst.",
          "Inga aktiva skulder hos Kronofogden hos de flesta aktörer.",
          "Eget bankkonto i ditt namn.",
        ],
      },
      { t: "p", text: "Har du redan flera krediter vägs den samlade månadskostnaden in. Ligger den över tio procent av nettolönen sjunker chansen märkbart." },

      { t: "h2", text: "Så ansöker du – steg för steg" },
      {
        t: "steps",
        title: "Från ansökan till tillgängligt utrymme",
        items: [
          "Välj kreditgivare och önskat kreditutrymme.",
          "Fyll i inkomst, boendekostnad och befintliga krediter.",
          "Signera ansökan med BankID.",
          "Kreditgivaren hämtar upplysning via Creditsafe eller Dun & Bradstreet.",
          "Beslut fattas ofta inom minuter.",
          "Signera kreditavtalet och gör ditt första uttag när du behöver.",
        ],
      },

      { t: "h2", text: "Hur snabbt får du pengarna?" },
      {
        t: "table",
        caption: "Utbetalningstid vid uttag från kontokredit",
        head: ["Metod", "Vardag", "Kväll och natt", "Helg"],
        rows: [
          ["Realtidsbetalning", "5–15 min", "5–15 min", "5–15 min"],
          ["Swish", "Minuter", "Minuter", "Minuter"],
          ["Bankgirering", "Samma bankdag", "Nästa bankdag", "Måndag"],
        ],
      },
      { t: "p", text: "Behöver du pengar omgående är realtidsutbetalning avgörande. Läs mer i vår guide om [snabblån med utbetalning direkt](/nyhet/snabblan-utbetalning-direkt)." },

      { t: "h2", text: "När en kontokredit är ett bra val" },
      { t: "p", text: "Kontokrediten är som bäst när behovet är oregelbundet och kortvarigt. Den som saknar buffert men har stabil inkomst får en säkerhetsventil som inte kostar något i vila." },
      {
        t: "proscons",
        pros: [
          "Kostar oftast ingenting när den inte används",
          "Ränta räknas dag för dag på utnyttjat belopp",
          "Utrymmet fylls på när du betalar tillbaka",
          "Ingen uppläggningsavgift hos de flesta aktörer",
        ],
        cons: [
          "Högre ränta än ett vanligt privatlån",
          "Lätt att utnyttja utrymmet utan plan",
          "Räknas som skuld i bolånekalkylen",
          "Minimibeloppet kan göra återbetalningen långsam",
        ],
      },

      { t: "h2", text: "När du hellre ska välja något annat" },
      { t: "p", text: "Behöver du 100 000 kr till en renovering är ett privatlån både billigare och tydligare. Har du redan flera dyra krediter är ett [samlingslån](/samla-lan) nästan alltid bättre än ytterligare ett kreditutrymme." },
      { t: "p", text: "Och ska du köpa bostad inom ett år bör du undvika nya krediter helt, eftersom hela det beviljade utrymmet räknas som skuld." },

      { t: "h2", text: "Andra alternativ värda att jämföra" },
      { t: "p", text: "Räcker inte en kontokredit, eller vill du ha lägre ränta på ett större belopp? Då är förmedlare och banklån ett bättre spår. Här är de närmaste alternativen." },
      { t: "lenders", ids: NAST_BAST, title: "Näst bästa alternativen – förmedlare och privatlån", note: "En ansökan skickas till flera banker samtidigt, vilket ofta ger lägre ränta än en kontokredit." },

      { t: "h2", text: "Vanliga misstag med kontokredit" },
      {
        t: "box",
        tone: "warning",
        title: "Fyra fällor att undvika",
        items: [
          "Att bara betala minimibeloppet – då tar återbetalningen flera år.",
          "Att se kreditutrymmet som en del av inkomsten.",
          "Att ha flera oanvända krediter liggande, som ändå belastar din kalkyl.",
          "Att teckna kredit för att betala en annan kredit.",
        ],
      },

      { t: "h2", text: "Minimibeloppet – den dolda kostnaden" },
      { t: "p", text: "De flesta kreditgivare kräver att du varje månad betalar 3–5 procent av saldot, dock lägst ett par hundralappar. Låter det billigt? Räkna igen. På 20 000 kr med tre procents minimibetalning och 19,9 procents ränta tar det över fyra år att bli skuldfri, och räntan blir dubbelt så hög som vid två års återbetalning." },
      { t: "p", text: "Sätt därför alltid en egen amorteringsplan i stället för att följa minimibeloppet." },

      { t: "h2", text: "Räntetak och kostnadstak – ditt skydd i lagen" },
      { t: "p", text: "Den nominella räntan får inte överstiga Riksbankens referensränta plus 20 procentenheter. De samlade kostnaderna för ränta, avgifter och dröjsmålsränta får aldrig överstiga kreditbeloppet. Utnyttjar du 20 000 kr kan kostnaden alltså aldrig bli mer än 20 000 kr." },
      {
        t: "box",
        tone: "info",
        title: "Det här gäller alltid",
        items: [
          "14 dagars ångerrätt enligt konsumentkreditlagen.",
          "Rätt att lösa krediten i förtid utan extra avgift.",
          "Obligatorisk kreditprövning – ingen seriös aktör hoppar över den.",
          "Kreditgivaren måste ha tillstånd från Finansinspektionen.",
        ],
      },

      { t: "h2", text: "Så påverkas din kreditvärdighet" },
      { t: "p", text: "Även om förfrågan inte syns i UC registreras krediten hos kreditupplysningsföretagen. Den påverkar alltså din samlade skuldbild, men inte antalet UC-förfrågningar." },
      {
        t: "table",
        caption: "Vad som väger tyngst i bedömningen",
        head: ["Faktor", "Påverkan", "Går att förbättra"],
        rows: [
          ["Betalningsanmärkning", "Mycket stor", "Nej – tiden avgör"],
          ["Skuld hos Kronofogden", "Mycket stor", "Ja – betala av"],
          ["Antal befintliga krediter", "Stor", "Ja – säg upp oanvända"],
          ["Inkomstens storlek", "Stor", "Ja – på sikt"],
          ["Antal förfrågningar", "Medel", "Ja – sprid ut ansökningarna"],
        ],
      },

      { t: "h2", text: "Kontokredit med betalningsanmärkning" },
      { t: "p", text: "Det är tuffare, men inte alltid kört. Är anmärkningen äldre än två år, skulden reglerad och inkomsten stabil gör flera nischaktörer en individuell bedömning. Läs mer i vår guide om [lån utan UC med många förfrågningar](/nyhet/lan-utan-uc-manga-forfragningar)." },
      { t: "p", text: "Har du däremot ett pågående mål hos Kronofogden får du i praktiken avslag överallt. Då är kommunens budget- och skuldrådgivning ett betydligt bättre första steg än en ny ansökan." },

      { t: "h2", text: "Så känner du igen ett bedrägeri" },
      { t: "p", text: "Löften om garanterat ja eller kredit helt utan prövning är alltid falska. Kontrollera företaget i Finansinspektionens register innan du lämnar några uppgifter." },
      {
        t: "box",
        tone: "warning",
        title: "Varningssignaler",
        items: [
          "Avgift som ska betalas i förskott för att krediten ska beviljas.",
          "Kontakt enbart via sms, WhatsApp eller Messenger.",
          "Löfte om kredit utan kreditupplysning.",
          "Ingen adress, inget organisationsnummer och inga fullständiga villkor.",
        ],
      },

      { t: "h2", text: "Så säger du upp en kontokredit" },
      {
        t: "steps",
        title: "Fyra steg",
        items: [
          "Betala av hela det utnyttjade saldot.",
          "Säg upp avtalet skriftligt eller i appen – muntligt räcker sällan.",
          "Begär skriftlig bekräftelse på att kontot är avslutat.",
          "Kontrollera efter en månad att krediten inte längre syns i din kreditupplysning.",
        ],
      },
      { t: "p", text: "Det här steget glöms ofta bort och är den vanligaste anledningen till att en gammal kredit dyker upp och sänker bolånekalkylen flera år senare." },

      { t: "h2", text: "Vanliga frågor från läsare" },
      { t: "h3", text: "Kan jag ha flera kontokrediter samtidigt?" },
      { t: "p", text: "Rent tekniskt ja, men varje kredit belastar din kalkyl med hela det beviljade utrymmet. Två oanvända krediter på 20 000 kr räknas alltså som 40 000 kr i skuld." },
      { t: "h3", text: "Höjs kreditgränsen automatiskt?" },
      { t: "p", text: "Flera aktörer erbjuder höjning efter några månaders skötsam betalning. Du måste normalt godkänna höjningen aktivt, och en ny kreditprövning görs." },
      { t: "h3", text: "Syns krediten i min deklaration?" },
      { t: "p", text: "Skulden förtrycks normalt inte, men betald ränta rapporteras av kreditgivaren och ger dig 30 procents ränteavdrag upp till 100 000 kr i ränta per år." },

      { t: "h2", text: "Så granskar vi kreditgivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, hur tydliga villkoren är, tillstånd hos Finansinspektionen och kundomdömen. Ingen aktör kan köpa sig till en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "En kontokredit utan UC ger flexibel beredskap utan att belasta din UC-historik, och kostar oftast ingenting när den ligger oanvänd. Priset är en högre ränta än på ett privatlån och en frestelse som är lätt att underskatta. Sätt en egen amorteringsplan, jämför effektiv ränta och säg upp krediten när behovet är borta." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Riksbanken](https://www.riksbank.se) – referensränta",
          "[Hallå konsument](https://www.hallakonsument.se) – oberoende vägledning",
        ],
      },
    ],
  },
};
